import { createAsyncThunk } from "@reduxjs/toolkit";
import axios from "../../utils/axios";
import {
    IGroupCategory,
    IPrimaryGroup,
    ISubGroup,
    ILedger,
    ICoaListResponse,
    ICoaFetchParams
} from "../../utils/coa.types";

// ==================== Group Categories ====================
export const fetchGroupCategories = createAsyncThunk(
    'coa/fetchGroupCategories',
    async (params: ICoaFetchParams = {}): Promise<ICoaListResponse<IGroupCategory>> => {
        const queryParams = new URLSearchParams();
        if (params.page !== undefined) queryParams.append('page', params.page.toString());
        if (params.limit !== undefined) queryParams.append('limit', params.limit.toString());
        if (params.search) queryParams.append('search', params.search);

        const response = await axios.get(`/api/v1/coa/group-categories?${queryParams.toString()}`);
        return response.data;
    }
);

export const createGroupCategory = createAsyncThunk(
    'coa/createGroupCategory',
    async (payload: Partial<IGroupCategory>): Promise<IGroupCategory> => {
        const response = await axios.post('/api/v1/coa/group-categories', payload);
        return response.data;
    }
);

export const updateGroupCategory = createAsyncThunk(
    'coa/updateGroupCategory',
    async ({ id, payload }: { id: string; payload: Partial<IGroupCategory> }): Promise<IGroupCategory> => {
        const response = await axios.put(`/api/v1/coa/group-categories/${id}`, payload);
        return response.data;
    }
);

export const deleteGroupCategory = createAsyncThunk(
    'coa/deleteGroupCategory',
    async (id: string): Promise<string> => {
        await axios.delete(`/api/v1/coa/group-categories/${id}`);
        return id;
    }
);

// ==================== Primary Groups ====================
export const fetchPrimaryGroups = createAsyncThunk(
    'coa/fetchPrimaryGroups',
    async (params: ICoaFetchParams = {}): Promise<ICoaListResponse<IPrimaryGroup>> => {
        const queryParams = new URLSearchParams();
        if (params.page !== undefined) queryParams.append('page', params.page.toString());
        if (params.limit !== undefined) queryParams.append('limit', params.limit.toString());
        if (params.search) queryParams.append('search', params.search);
        if (params.groupCategoryId) queryParams.append('groupCategoryId', params.groupCategoryId);
        if (params.filterOwn !== undefined) queryParams.append('filterOwn', params.filterOwn.toString());

        const response = await axios.get(`/api/v1/coa/primary-groups?${queryParams.toString()}`);
        return response.data;
    }
);

export const createPrimaryGroup = createAsyncThunk(
    'coa/createPrimaryGroup',
    async (payload: Partial<IPrimaryGroup>): Promise<IPrimaryGroup> => {
        const response = await axios.post('/api/v1/coa/primary-groups', payload);
        return response.data;
    }
);

export const updatePrimaryGroup = createAsyncThunk(
    'coa/updatePrimaryGroup',
    async ({ id, payload }: { id: string; payload: Partial<IPrimaryGroup> }): Promise<IPrimaryGroup> => {
        const response = await axios.put(`/api/v1/coa/primary-groups/${id}`, payload);
        return response.data;
    }
);

export const deletePrimaryGroup = createAsyncThunk(
    'coa/deletePrimaryGroup',
    async (id: string): Promise<string> => {
        await axios.delete(`/api/v1/coa/primary-groups/${id}`);
        return id;
    }
);

export const fetchNextSequencePreview = createAsyncThunk(
    'coa/fetchNextSequencePreview',
    async (groupCategoryId: string): Promise<{ seqNumber: string; accountingCode: string }> => {
        const response = await axios.get(`/api/v1/coa/primary-groups/next-sequence?groupCategoryId=${groupCategoryId}`);
        return response.data;
    }
);

// ==================== Sub Groups ====================
export const fetchSubGroups = createAsyncThunk(
    'coa/fetchSubGroups',
    async (params: ICoaFetchParams = {}): Promise<ICoaListResponse<ISubGroup>> => {
        const queryParams = new URLSearchParams();
        if (params.page !== undefined) queryParams.append('page', params.page.toString());
        if (params.limit !== undefined) queryParams.append('limit', params.limit.toString());
        if (params.search) queryParams.append('search', params.search);
        if (params.primaryGroupId) queryParams.append('primaryGroupId', params.primaryGroupId);
        if (params.groupCategoryId) queryParams.append('groupCategoryId', params.groupCategoryId);
        if (params.filterOwn !== undefined) queryParams.append('filterOwn', params.filterOwn.toString());

        const response = await axios.get(`/api/v1/coa/sub-groups?${queryParams.toString()}`);
        return response.data;
    }
);

export const createSubGroup = createAsyncThunk(
    'coa/createSubGroup',
    async (payload: Partial<ISubGroup>): Promise<ISubGroup> => {
        const response = await axios.post('/api/v1/coa/sub-groups', payload);
        return response.data;
    }
);

export const updateSubGroup = createAsyncThunk(
    'coa/updateSubGroup',
    async ({ id, payload }: { id: string; payload: Partial<ISubGroup> }): Promise<ISubGroup> => {
        const response = await axios.put(`/api/v1/coa/sub-groups/${id}`, payload);
        return response.data;
    }
);

export const deleteSubGroup = createAsyncThunk(
    'coa/deleteSubGroup',
    async (id: string): Promise<string> => {
        await axios.delete(`/api/v1/coa/sub-groups/${id}`);
        return id;
    }
);

export const fetchSubGroupNextSequencePreview = createAsyncThunk(
    'coa/fetchSubGroupNextSequencePreview',
    async (primaryGroupId: string): Promise<{ seqNumber: string; accountingCode: string }> => {
        const response = await axios.get(`/api/v1/coa/sub-groups/next-sequence?primaryGroupId=${primaryGroupId}`);
        return response.data;
    }
);

// ==================== Ledgers ====================
export const fetchLedgers = createAsyncThunk(
    'coa/fetchLedgers',
    async (params: ICoaFetchParams = {}): Promise<ICoaListResponse<ILedger>> => {
        const queryParams = new URLSearchParams();
        if (params.page !== undefined) queryParams.append('page', params.page.toString());
        if (params.limit !== undefined) queryParams.append('limit', params.limit.toString());
        if (params.search) queryParams.append('search', params.search);
        if (params.subGroupId) queryParams.append('subGroupId', params.subGroupId);
        if (params.primaryGroupId) queryParams.append('primaryGroupId', params.primaryGroupId);
        if (params.groupCategoryId) queryParams.append('groupCategoryId', params.groupCategoryId);
        if (params.filterOwn !== undefined) queryParams.append('filterOwn', params.filterOwn.toString());

        const response = await axios.get(`/api/v1/coa/ledgers?${queryParams.toString()}`);
        return response.data;
    }
);

export const createLedger = createAsyncThunk(
    'coa/createLedger',
    async (payload: Partial<ILedger>): Promise<ILedger> => {
        const response = await axios.post('/api/v1/coa/ledgers', payload);
        return response.data;
    }
);

export const updateLedger = createAsyncThunk(
    'coa/updateLedger',
    async ({ id, payload }: { id: string; payload: Partial<ILedger> }): Promise<ILedger> => {
        const response = await axios.put(`/api/v1/coa/ledgers/${id}`, payload);
        return response.data;
    }
);

export const deleteLedger = createAsyncThunk(
    'coa/deleteLedger',
    async (id: string): Promise<string> => {
        await axios.delete(`/api/v1/coa/ledgers/${id}`);
        return id;
    }
);

export const fetchLedgerNextSequencePreview = createAsyncThunk(
    'coa/fetchLedgerNextSequencePreview',
    async ({ primaryGroupId, subGroupId }: { primaryGroupId: string; subGroupId?: string }): Promise<{ seqNumber: string; accountingCode: string }> => {
        const queryParams = new URLSearchParams();
        queryParams.append('primaryGroupId', primaryGroupId);
        if (subGroupId) {
            queryParams.append('subGroupId', subGroupId);
        }
        const response = await axios.get(`/api/v1/coa/ledgers/next-sequence?${queryParams.toString()}`);
        return response.data;
    }
);
